import { computed } from "vue";
import {
  useRoute,
  type RouteLocationNormalizedLoaded,
  type RouteRecordRaw,
} from "vue-router";
import { navbarRoutes } from "@/router/sidenav_routes";

type MatchedRecord = RouteLocationNormalizedLoaded["matched"][number];

const NAVBAR_FOLDER_NAMES = new Set(
  navbarRoutes.map((record) => String(record.name)),
);

function navLabel(record: MatchedRecord | RouteRecordRaw): string | undefined {
  return record.meta?.text ?? record.meta?.title;
}

function isListedInSubmenu(record: MatchedRecord | RouteRecordRaw): boolean {
  return !record.meta?.excludeFromNavbar && !record.path.includes(":");
}

// The sidebar lists a submenu under a navbar folder's child (e.g. Analytics >
// Ad Performance > Overview / Ads). On those leaves the page title reads as
// "<submenu owner> <item>" so "Overview" never stands alone.
export function resolveRouteTitle(
  route: Pick<RouteLocationNormalizedLoaded, "matched" | "meta">,
  folderNames: ReadonlySet<string> = NAVBAR_FOLDER_NAMES,
): string {
  const { matched } = route;
  const ownerIndex = matched.findIndex(
    (_, index) => index > 0 && folderNames.has(String(matched[index - 1].name)),
  );
  const owner = ownerIndex === -1 ? undefined : matched[ownerIndex];
  const item = owner && matched[ownerIndex + 1];
  const leaf = matched.at(-1);
  if (!owner || !item || !leaf) return route.meta.title;

  const listedItems = (owner.children ?? []).filter(isListedInSubmenu);
  const leafIsItemOrItsIndex = leaf === item || leaf.path === item.path;
  if (
    listedItems.length < 2 ||
    !isListedInSubmenu(item) ||
    !leafIsItemOrItsIndex
  ) {
    return route.meta.title;
  }

  const ownerLabel = navLabel(owner);
  const itemLabel = navLabel(item);
  if (!ownerLabel || !itemLabel) return route.meta.title;
  return `${ownerLabel} ${itemLabel}`;
}

export function useRouteTitle() {
  const route = useRoute();
  return computed(() => resolveRouteTitle(route));
}
